import { z } from 'zod'
import { localeSchema, pageQuerySchema } from './common.js'
import type { ProjectStatus, ProjectUserRef } from './projects.js'
import type { CostCategoryRef, ProjectRefLite } from './projects-costs.js'

/**
 * Project reports (projects/06 §5.14; P3_BREAKDOWN.md). `GET /reports/projects/<report>`: projects.reports,
 * over the projects the reader reaches under projects.financials (every column is money). Each takes
 * `?format=csv&lang=` for an export of the same rows. Forecast columns join with the stored insights (P5).
 */

const reportFilters = {
  q: z.string().trim().max(200).optional(),
  status: z.enum(['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CLOSED', 'CANCELLED']).optional(),
  /** Default: open projects (PLANNING, ACTIVE, ON_HOLD, COMPLETED). */
  includeClosed: z.stringbool().optional(),
  customerId: z.uuid().optional(),
  managerId: z.uuid().optional(),
  orgUnitId: z.uuid().optional(),
  typeId: z.uuid().optional(),
}
const exportFields = { format: z.enum(['json', 'csv']).default('json'), lang: localeSchema.default('en') }

/** At most this many rows in one export. */
export const PROJECT_REPORT_EXPORT_MAX_ROWS = 20_000

// ── profitability ────────────────────────────────────────────────────────────

export const PROFITABILITY_SORT_FIELDS = [
  'code',
  'contract',
  'billed',
  'cost',
  'margin',
  'marginPct',
] as const
export const profitabilityQuerySchema = pageQuerySchema.extend({ ...reportFilters, ...exportFields })
export type ProfitabilityQuery = z.input<typeof profitabilityQuerySchema>

export interface ProfitabilityRow {
  project: ProjectRefLite
  customer: { id: string; code: string; name: string; nameAr: string | null } | null
  manager: ProjectUserRef
  status: ProjectStatus
  contractValue: string | null
  /** The APPROVED budget's cost. */
  budget: string | null
  /** Ex-VAT revenue to date (REVENUE facts). */
  billed: string
  /** Actual cost to date (COST facts). */
  cost: string
  /** billed − cost. */
  margin: string
  /** margin ÷ billed × 100, one decimal; null when nothing is billed. */
  marginPct: string | null
  /** From the stored insights (P5): forecast margin %, null until measured. Optional for older fixtures. */
  forecastMarginPct?: string | null
  collected: string
  outstanding: string
}
export interface ProfitabilityReport {
  items: ProfitabilityRow[]
  page: number
  pageSize: number
  total: number
  totals: {
    contractValue: string
    budget: string
    billed: string
    cost: string
    margin: string
    collected: string
    outstanding: string
  }
}

// ── budget against actual ────────────────────────────────────────────────────

/** One row per project and cost category with a budget, cost or commitment, as at `asOf` (default today). */
export const budgetVsActualQuerySchema = pageQuerySchema.extend({
  ...reportFilters,
  ...exportFields,
  asOf: z.iso.date().optional(),
  projectId: z.uuid().optional(),
  costCategoryId: z.uuid().optional(),
  /** Only rows at or above the warning level. */
  overOnly: z.stringbool().optional(),
})
export type BudgetVsActualQuery = z.input<typeof budgetVsActualQuerySchema>

export interface BudgetVsActualRow {
  project: ProjectRefLite
  costCategory: CostCategoryRef | null
  budget: string
  committed: string
  actual: string
  remaining: string
  usePct: string | null
  band: 'NONE' | 'NORMAL' | 'WARN' | 'CRITICAL'
}
export interface BudgetVsActualReport {
  asOf: string
  items: BudgetVsActualRow[]
  page: number
  pageSize: number
  total: number
  totals: { budget: string; committed: string; actual: string; remaining: string }
}

// ── unbilled work ────────────────────────────────────────────────────────────

/** Per project: READY billing items and billable T&M not yet invoiced (03 §3, §6). */
export const unbilledWorkQuerySchema = pageQuerySchema.extend({ ...reportFilters, ...exportFields })
export type UnbilledWorkQuery = z.input<typeof unbilledWorkQuerySchema>

export interface UnbilledWorkRow {
  project: ProjectRefLite
  customer: { id: string; code: string; name: string; nameAr: string | null } | null
  readyItems: number
  readyAmount: string
  /** Oldest READY date (planned date, achievement or "Mark ready"). */
  readySince: string | null
  tmAmount: string
  tmOldestOn: string | null
  total: string
}
export interface UnbilledWorkReport {
  items: UnbilledWorkRow[]
  page: number
  pageSize: number
  total: number
  totals: { readyAmount: string; tmAmount: string; total: string }
}

// ── utilisation (P6) ─────────────────────────────────────────────────────────

/**
 * `GET /reports/projects/utilisation?from&to` (projects.reports): hours by person over the period, from the
 * time entries on projects the reader reaches under projects.read. Hours need no money permission; cost
 * needs projects.rates and billable value projects.financials (P6 decision 15).
 */
export const UTILISATION_SORT_FIELDS = ['name', 'approved', 'billablePct', 'utilisationPct'] as const
export const utilisationQuerySchema = pageQuerySchema.extend({
  from: z.iso.date(),
  to: z.iso.date(),
  orgUnitId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  userId: z.uuid().optional(),
  ...exportFields,
})
export type UtilisationQuery = z.input<typeof utilisationQuerySchema>
export interface UtilisationRow {
  user: ProjectUserRef
  /** Σ allocation × working days in the period × `time.hoursPerDay` over the person's projects; null without any allocation. */
  expectedMinutes: number | null
  approvedMinutes: number
  submittedMinutes: number
  /** Of the approved and submitted minutes. */
  billableMinutes: number
  nonBillableMinutes: number
  /** billable ÷ (approved + submitted) × 100, one decimal; null with no time. */
  billablePct: string | null
  /** (approved + submitted) ÷ expected × 100, one decimal; null without expected hours. */
  utilisationPct: string | null
  /** Approved cost (projects.rates) and billable value (projects.financials); null otherwise. */
  costAmount: string | null
  billableValue: string | null
  projects: number
}
export interface UtilisationReport {
  from: string
  to: string
  items: UtilisationRow[]
  page: number
  pageSize: number
  total: number
  totals: {
    expectedMinutes: number | null
    approvedMinutes: number
    submittedMinutes: number
    billableMinutes: number
    nonBillableMinutes: number
    costAmount: string | null
    billableValue: string | null
  }
}
